<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">
        Lista de Empresas
      </h1>
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <!-- Campo de busca -->
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchTerm"
              placeholder="Pesquisar por nome"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="loadEnterprises"
            />
          </v-col>
        </v-row>

        <!-- DataTable -->
        <v-data-table-server
          v-model:options="options"
          :headers="columns"
          :items="enterprises"
          :items-length="totalItems"
          :loading="isLoading"
          :no-data-text="noDataMessage"
          class="elevation-0"
          density="comfortable"
        >
          <template v-slot:item.name="{ item }">
            <span>{{ item.name }}</span>
          </template>

          <template v-slot:item.email="{ item }">
            <span>{{ item.email }}</span>
          </template>

          <template v-slot:item.whatsapp="{ item }">
            <span>{{ formatPhone(item.whatsapp) }}</span>
          </template>

          <template v-slot:item.phone="{ item }">
            <span>{{ item.phone }}</span>
          </template>

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon
              icon="mdi-pencil"
              color="warning"
              class="mr-2" size="large"
              @click="handleEdit(item)"
              title="Editar"
            />
          </template>
        </v-data-table-server>

        <!-- Erro -->
        <v-alert
          v-if="hasError && !isLoading"
          type="error"
          variant="tonal"
          class="mt-4 text-center"
        >
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingOverlay } from "@/store/loadingOverlay";

const router = useRouter();
const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();

const enterprises = ref([]);
const totalItems = ref(0);
const isLoading = ref(true);
const hasError = ref(false);
const searchTerm = ref("");

const options = ref({
  page: 1,
  itemsPerPage: 10,
  sortBy: [],
});

// Map cores status
const isActiveTagMap = {
  0: "error",
  1: "success",
};

// Colunas da tabela
const columns = [
  { title: "Nome", key: "name", sortable: true },
  { title: "Email", key: "email", sortable: true },
  { title: "Whatsapp", key: "whatsapp", sortable: true },
  { title: "Telefone", key: "phone", sortable: true },
  { title: "Situação", key: "is_active", sortable: true },
  { title: "Ações", key: "actions", sortable: false },
];

// Texto dinâmico quando vazio
const noDataMessage = ref("Nenhuma empresa encontrada.");

// Carregar dados da API
const loadEnterprises = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadingOverlay.startLoading();

  try {
    const { page, itemsPerPage, sortBy } = options.value;

    const response = await api.get("/enterprises", {
      params: {
        page,
        per_page: itemsPerPage,
        search: searchTerm.value,
        sortBy: sortBy[0]?.key || null,
        sortOrder: sortBy[0]?.order || null,
      },
    });

    enterprises.value = response.data.data;
    totalItems.value = response.data.total || response.data.meta?.total || 0;
  } catch (error) {
    console.error("Erro ao carregar empresas:", error);
    hasError.value = true;
    toastStore.showToast({
      message:
        error?.response?.data?.error ||
        "Ocorreu um erro ao carregar a lista de empresas.",
      color: "error",
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

// Editar
const handleEdit = (row) => {
  router.push({ name: "enterprise-edit", params: { id: row.id } });
};

// Formatadores
function formatIsActive(is_active) {
  switch (is_active) {
    case 0:
      return "Desativado";
    case 1:
      return "Ativo";
    default:
      return is_active;
  }
}

function formatPhone(phone) {
  if (!phone) return "";

  const cleaned = ("" + phone).replace(/\D/g, "");

  const match11 = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match11) return `(${match11[1]}) ${match11[2]}-${match11[3]}`;

  const match10 = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
  if (match10) return `(${match10[1]}) ${match10[2]}-${match10[3]}`;

  return phone;
}

// Recarregar ao mudar paginação ou ordenação
watch(options, () => {
  loadEnterprises();
}, { deep: true });

// Inicial
onMounted(() => {
  loadEnterprises();
});
</script>
